import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutlined";
import { Paper, Stack, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import MetaChip from "../../../components/UIToolkit/Badges/MetaChip";
import { RADIUS } from "../../../theme/tokens";
import type { PbjPlanCost } from "../../../utils/models";
import { PlanCostAndSavingsBody } from "./PlanCostAndSavingsBody";
import { PlanInvestmentBody } from "./PlanInvestmentBody";

type Props = {
	cost: PbjPlanCost;
	// Which directions the plan's committed levers move in. The layout follows the plan's SHAPE, not
	// its priced totals: a plan whose window has no measurable days prices every figure at $0, and a
	// cut plan read off `totalSavings > 0` would then fall back to the add-only Investment copy.
	directions: { adds: boolean; cuts: boolean };
};

export const PlanCostPanel: FC<Props> = ({ cost, directions }) => {
	const { t } = useTranslation();
	// A plan with no cuts renders exactly the original single-bar Investment panel. Only a plan that
	// cuts gets the cost-and-savings layout.
	const hasCuts = directions.cuts;
	// `spendSoFar` is `max(0, delivered) x wage`, so zero spend means the department has delivered
	// nothing or gone backwards. Budget adherence carries no information in that state: `0 <=
	// budgetToDate` is trivially true, so a green "On budget" would be reporting the ABSENCE of
	// delivery as financial health — and the worse a plan goes, the healthier this panel reads. The
	// verdict is withheld until there is spend to judge.
	const hasSpend = cost.spendSoFar > 0;
	const hasSavings = cost.savingsSoFar > 0;
	// With cuts, savings are movement too: a cut-only plan never spends, so waiting on spend alone
	// would withhold its verdict forever.
	const hasChange = hasSpend || (hasCuts && hasSavings);
	// One decision, three consistent outputs — tone, icon and copy cannot drift out of step the way
	// three separate conditionals on the same predicate can.
	const budgetBadge = !hasChange
		? { tone: "neutral" as const, icon: <RemoveCircleOutlineIcon />, labelKey: hasCuts ? "pbj_model.tracker.no_change_yet" : "pbj_model.tracker.no_spend_yet" }
		: cost.onBudget
			? { tone: "success" as const, icon: <CheckCircleOutlineIcon />, labelKey: "pbj_model.tracker.on_budget" }
			: // Behind the net budget. With real spend that is overspending; with none — a cut-only plan, or
				// a mixed one whose additions have not started — nothing was overspent, the savings are
				// simply behind, and "Over budget" beside $0 spent would not say what happened.
				{
					tone: "error" as const,
					icon: <ErrorOutlineIcon />,
					labelKey: hasSpend ? "pbj_model.tracker.over_budget" : "pbj_model.tracker.behind_on_savings",
				};

	const share = (cost.elapsedShare * 100).toFixed(0);

	return (
		<Paper
			variant="outlined"
			sx={{ p: 3, borderRadius: `${RADIUS.POPOVER}px`, height: "100%", display: "flex", flexDirection: "column", gap: 2 }}
			data-testid="pbj-model-plan-cost"
		>
			<Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: 1 }}>
				<Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.6, color: "text.secondary" }}>
					{t(hasCuts ? "pbj_model.tracker.cost_and_savings" : "pbj_model.tracker.investment")}
				</Typography>
				<MetaChip tone={budgetBadge.tone} icon={budgetBadge.icon} label={t(budgetBadge.labelKey)} dataTestId="pbj-model-plan-on-budget" />
			</Stack>

			{hasCuts ? (
				<PlanCostAndSavingsBody cost={cost} isMixed={directions.adds} hasSpend={hasSpend} hasSavings={hasSavings} share={share} />
			) : (
				<PlanInvestmentBody cost={cost} hasSpend={hasSpend} share={share} />
			)}
		</Paper>
	);
};
